import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../styles/LanguageTypography.css';
import {
    faUserTie,
    faPhone,
    faEnvelope,
    faAddressCard,
    faHandshake,
    faCalendarAlt,
    faFileUpload,
    faRupeeSign,
    faPercent,
    faAlignLeft,
    faStickyNote,
    faSave,
    faTimes,
    faCheckCircle,
    faExclamationTriangle,
    faExchangeAlt,
    faSpinner,
    faChevronDown,
    faBuilding,
    faStore,
    faUser,
    faIdCard,
    faClock,
    faFileContract,
    faMoneyBillWave,
    faChartLine,
    faInfoCircle,
    faSearch,
    faPlus,
    faEdit,
    faTrash,
    faMoneyCheckDollar
} from '@fortawesome/free-solid-svg-icons';

// ============================================================
// ONLY ENGLISH AND URDU LANGUAGES
// ============================================================
const RTL_LANGUAGES = new Set(['ur']);
const SUPPORTED_LANGUAGES = new Set(['en', 'ur']);

const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    const normalized = lang.toLowerCase().split('-')[0];
    return SUPPORTED_LANGUAGES.has(normalized) ? normalized : 'en';
};

// ============================================================
// HARDCODED BILINGUAL UI DICTIONARY - ENGLISH & URDU ONLY
// ============================================================
const UI = {
    en: {
        addPartner: 'Add Partner',
        editPartner: 'Edit Partner',
        fillDetails: 'Fill in the partner details below',
        partnerDetails: 'Partner Details',
        partnerName: 'Partner Name',
        phone: 'Phone Number',
        email: 'Email Address',
        address: 'Address',
        partnershipType: 'Partnership Type',
        partnershipDuration: 'Partnership Duration',
        partnershipContract: 'Partnership Contract',
        investmentAmount: 'Investment Amount',
        profitPercentage: 'Profit %',
        description: 'Description',
        notes: 'Notes / Remarks',
        contractStartDate: 'Contract Start Date',
        contractEndDate: 'Contract End Date',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        suspended: 'Suspended',
        terminated: 'Terminated',
        savePartner: 'Save Partner',
        updatePartner: 'Update Partner',
        clearButton: 'Clear',
        adding: 'Adding...',
        updating: 'Updating...',
        partnerSaved: 'Partner saved successfully!',
        partnerUpdated: 'Partner updated successfully!',
        failedAdd: 'Failed to save partner',
        failedUpdate: 'Failed to update partner',
        networkError: 'Network error occurred',
        nameRequired: 'Partner name is required',
        nameTooShort: 'Name must be at least 2 characters',
        invalidEmail: 'Please enter a valid email address',
        invalidInvestment: 'Investment amount cannot be negative',
        invalidProfit: 'Profit percentage must be between 0 and 100',
        contractEndBeforeStart: 'Contract end date cannot be before start date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        optional: 'Optional',
        required: 'Required',
        allType: 'All Type Partnership',
        productBased: 'Product Based Partnership',
        uploadContract: 'Upload Contract',
        chooseFile: 'Choose File',
        noFileSelected: 'No file selected',
        fileUploadSuccess: 'Contract uploaded successfully',
        fileUploadFailed: 'Failed to upload contract',
        selectType: 'Select Partnership Type',
        selectDuration: 'Select Duration',
        enterPartnerName: 'Enter partner name',
        enterPhone: 'Enter phone number',
        enterEmail: 'Enter email address',
        enterAddress: 'Enter address',
        enterDescription: 'Enter description',
        enterNotes: 'Enter notes / remarks',
        enterInvestment: 'Enter investment amount',
        enterProfit: 'Enter profit percentage',
        selectProducts: 'Select Products',
        selectedProducts: 'Selected Products',
        selected: 'selected',
        searchProducts: 'Search products...',
        loadingProducts: 'Loading products...',
        noProductsAvailable: 'No products available. Please add products first.',
        noProductsFound: 'No products found matching your search.',
        clearAll: 'Clear All',
        productName: 'Product Name',
        productBatch: 'Batch',
        productQty: 'Qty',
        expensesSalariesIncluded: 'Expenses/Salaries Included',
        includeExpenses: 'Include Expenses & Salary',
        doNotIncludeExpenses: 'Do not include Expenses & Salary',
        selectExpensesOption: 'Select option...',
        clearForm: 'Clear Form',
        noCategoriesFound: 'No categories found',
        switchShopTooltip: 'Switch Shop',
        clearFormTooltip: 'Clear Form',
        unnamed: 'Unnamed',
        draftExists: 'A saved draft exists',
        viewDrafts: 'View Drafts',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        saveDraft: 'Save Draft',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        cannotSaveEmptyDraft: 'Cannot save an empty draft',
        draftSaved: 'Draft saved successfully!',
        draftDeleted: 'Draft deleted successfully!',
        save: 'Save',
        goBack: 'Go Back',
        unnamedPartner: 'Unnamed Partner',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        products: 'products',
        noTypeFound: 'No types found',
        noDurationFound: 'No durations found',
        noOptionFound: 'No options found',
    },
    ur: {
        // ============================================================
        // NATURAL URDU - NO "اپشنل" anywhere
        // ============================================================
        addPartner: 'پارٹنر شامل کریں',
        editPartner: 'پارٹنر میں ترمیم کریں',
        fillDetails: 'براہ کرم پارٹنر کی تفصیلات درج کریں',
        partnerDetails: 'پارٹنر کی تفصیلات',
        partnerName: 'پارٹنر کا نام',
        phone: 'فون نمبر',
        email: 'ای میل ایڈریس',
        address: 'پتہ',
        partnershipType: 'شراکت کی قسم',
        partnershipDuration: 'شراکت کی مدت',
        partnershipContract: 'شراکت کا معاہدہ',
        investmentAmount: 'سرمایہ کاری کی رقم',
        profitPercentage: 'منافع کا فیصد',
        description: 'تفصیل',
        notes: 'نوٹس / ریمارکس',
        contractStartDate: 'معاہدے کی آغاز کی تاریخ',
        contractEndDate: 'معاہدے کی اختتامی تاریخ',
        status: 'حالت',
        active: 'فعال',
        inactive: 'غیر فعال',
        suspended: 'معطل',
        terminated: 'ختم شدہ',
        savePartner: 'پارٹنر محفوظ کریں',
        updatePartner: 'پارٹنر اپ ڈیٹ کریں',
        clearButton: 'صاف کریں',
        adding: 'شامل ہو رہا ہے...',
        updating: 'اپ ڈیٹ ہو رہا ہے...',
        partnerSaved: 'پارٹنر کامیابی سے محفوظ ہو گیا!',
        partnerUpdated: 'پارٹنر کامیابی سے اپ ڈیٹ ہو گیا!',
        failedAdd: 'پارٹنر محفوظ کرنے میں ناکامی',
        failedUpdate: 'پارٹنر اپ ڈیٹ کرنے میں ناکامی',
        networkError: 'نیٹ ورک کی خرابی',
        nameRequired: 'پارٹنر کا نام ضروری ہے',
        nameTooShort: 'نام کم از کم 2 حروف کا ہونا چاہیے',
        invalidEmail: 'براہ کرم درست ای میل ایڈریس درج کریں',
        invalidInvestment: 'سرمایہ کاری کی رقم منفی نہیں ہو سکتی',
        invalidProfit: 'منافع کا فیصد 0 اور 100 کے درمیان ہونا چاہیے',
        contractEndBeforeStart: 'معاہدے کی اختتامی تاریخ آغاز کی تاریخ سے پہلے نہیں ہو سکتی',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        selectShop: 'پہلے دکان منتخب کریں',
        currentShop: 'موجودہ دکان',
        noShopSelected: 'کوئی دکان منتخب نہیں۔ براہ کرم پہلے دکان تبدیل کریں',
        switchShop: 'دکان تبدیل کریں',
        shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
        shopSwitchFailed: 'دکان تبدیل کرنے میں ناکامی',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
        noShops: 'کوئی دکان دستیاب نہیں',
        current: 'موجودہ',
        // ============================================================
        // REMOVED: optional completely - no "اپشنل" anywhere
        // ============================================================
        optional: '',
        required: 'ضروری',
        allType: 'تمام قسم کی شراکت',
        productBased: 'مصنوعات کی بنیاد پر شراکت',
        uploadContract: 'معاہدہ اپ لوڈ کریں',
        chooseFile: 'فائل منتخب کریں',
        noFileSelected: 'کوئی فائل منتخب نہیں',
        fileUploadSuccess: 'معاہدہ کامیابی سے اپ لوڈ ہو گیا',
        fileUploadFailed: 'معاہدہ اپ لوڈ کرنے میں ناکامی',
        selectType: 'شراکت کی قسم منتخب کریں',
        selectDuration: 'مدت منتخب کریں',
        enterPartnerName: 'پارٹنر کا نام درج کریں',
        enterPhone: 'فون نمبر درج کریں',
        enterEmail: 'ای میل ایڈریس درج کریں',
        enterAddress: 'پتہ درج کریں',
        enterDescription: 'تفصیل درج کریں',
        enterNotes: 'نوٹس / ریمارکس درج کریں',
        enterInvestment: 'سرمایہ کاری کی رقم درج کریں',
        enterProfit: 'منافع کا فیصد درج کریں',
        selectProducts: 'مصنوعات منتخب کریں',
        selectedProducts: 'منتخب شدہ مصنوعات',
        selected: 'منتخب',
        searchProducts: 'مصنوعات تلاش کریں...',
        loadingProducts: 'مصنوعات لوڈ ہو رہی ہیں...',
        noProductsAvailable: 'کوئی مصنوعات دستیاب نہیں۔ براہ کرم پہلے مصنوعات شامل کریں۔',
        noProductsFound: 'آپ کی تلاش سے مماثل کوئی مصنوعات نہیں ملی۔',
        clearAll: 'سب صاف کریں',
        productName: 'مصنوعات کا نام',
        productBatch: 'بیچ',
        productQty: 'مقدار',
        expensesSalariesIncluded: 'اخراجات/تنخواہیں شامل ہیں',
        includeExpenses: 'اخراجات اور تنخواہیں شامل کریں',
        doNotIncludeExpenses: 'اخراجات اور تنخواہیں شامل نہ کریں',
        selectExpensesOption: 'آپشن منتخب کریں...',
        clearForm: 'فارم صاف کریں',
        noCategoriesFound: 'کوئی کیٹیگری نہیں ملی',
        switchShopTooltip: 'دکان تبدیل کریں',
        clearFormTooltip: 'فارم صاف کریں',
        unnamed: 'بے نام',
        draftExists: 'پہلے سے محفوظ ڈرافٹ موجود ہے',
        viewDrafts: 'ڈرافٹس دیکھیں',
        drafts: 'ڈرافٹس',
        savedDrafts: 'محفوظ ڈرافٹس',
        noDraftsSaved: 'کوئی ڈرافٹ محفوظ نہیں',
        saveDraft: 'ڈرافٹ محفوظ کریں',
        draftLoaded: 'ڈرافٹ لوڈ ہو گیا',
        draftCleared: 'ڈرافٹ صاف ہو گیا',
        cannotSaveEmptyDraft: 'خالی ڈرافٹ محفوظ نہیں ہو سکتا',
        draftSaved: 'ڈرافٹ کامیابی سے محفوظ ہو گیا!',
        draftDeleted: 'ڈرافٹ کامیابی سے حذف ہو گیا!',
        save: 'محفوظ کریں',
        goBack: 'واپس جائیں',
        unnamedPartner: 'بے نام پارٹنر',
        selectAll: 'سب منتخب کریں',
        deselectAll: 'سب ہٹائیں',
        products: 'مصنوعات',
        noTypeFound: 'کوئی قسم نہیں ملی',
        noDurationFound: 'کوئی مدت نہیں ملی',
        noOptionFound: 'کوئی آپشن نہیں ملا',
    }
};

// ============================================================
// TOAST NOTIFICATION - POSITION BASED ON LANGUAGE
// ============================================================
const ToastNotification = ({ toast, setToast, t, isRTL }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            case 'info': return faCheckCircle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return t.success || 'Success';
            case 'error': return t.error || 'Error';
            case 'warning': return t.warning || 'Warning';
            case 'info': return 'Info';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const positionStyle = isRTL
        ? { bottom: '20px', left: '20px', right: 'auto' }
        : { bottom: '20px', right: '20px', left: 'auto' };

    return (
        <div className={`ozui_toast-notification ${toast.type}`} style={positionStyle}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ============================================================
// SHOP SELECTOR MODAL
// ============================================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, t, loading, isRTL }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <div className="ozui_modal-header">
                    <h3>{t.switchShop}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{t.loadingShops}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{t.noShops}</div>
                    ) : (
                        shops.map((shop) => (
                            <div
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {t.current}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const SolarPartners = ({ partnerId = null, onSuccess = null }) => {
    const auth = useAuth();
    const { user, currentShop, switchShop, getShopsForSwitch } = auth;

    // ============================================================
    // LANGUAGE DETECTION
    // ============================================================
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return normalizeLanguage(
            auth?.currentLanguage ||
            auth?.language ||
            user?.language ||
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') ||
            'en'
        );
    });

    const isRTL = currentLanguage === 'ur';
    const t = UI[currentLanguage] || UI.en;

    const partnerNameRef = useRef(null);

    // ============================================================
    // OPTIONS
    // ============================================================
    const expensesOptions = [
        { value: true, label: t.includeExpenses || 'Include Expenses & Salary' },
        { value: false, label: t.doNotIncludeExpenses || 'Do not include Expenses & Salary' }
    ];

    const partnershipTypeOptions = [
        { value: 'ALL', label: t.allType || 'All Type Partnership' },
        { value: 'Product Based', label: t.productBased || 'Product Based Partnership' }
    ];

    const durationOptions = [
        { value: '', label: t.selectDuration || 'Select Duration' },
        { value: '3_months', label: '3 Months' },
        { value: '6_months', label: '6 Months' },
        { value: '1_year', label: '1 Year' },
        { value: '2_years', label: '2 Years' },
        { value: '3_years', label: '3 Years' },
        { value: '5_years', label: '5 Years' },
        { value: 'lifetime', label: 'Lifetime' },
        { value: 'custom', label: 'Custom Duration' }
    ];

    // ============================================================
    // STATE
    // ============================================================
    const [formData, setFormData] = useState({
        partner_name: '',
        phone: '',
        email: '',
        address: '',
        partnership_type: 'ALL',
        partnership_duration: '',
        partnership_contract: null,
        investment_amount: '',
        profit_percentage: '',
        description: '',
        notes: '',
        contract_start_date: '',
        contract_end_date: '',
        status: 'active',
        expenses_salaries_included: false,
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        return document.body.classList.contains('dark-mode') || localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    // Manual dropdown states
    const [showPartnershipTypeDropdown, setShowPartnershipTypeDropdown] = useState(false);
    const [partnershipTypeSearchTerm, setPartnershipTypeSearchTerm] = useState('');
    const [showDurationDropdown, setShowDurationDropdown] = useState(false);
    const [durationSearchTerm, setDurationSearchTerm] = useState('');
    const [showExpensesDropdown, setShowExpensesDropdown] = useState(false);
    const [expensesSearchTerm, setExpensesSearchTerm] = useState('');

    // Refs for dropdowns
    const partnershipTypeDropdownRef = useRef(null);
    const durationDropdownRef = useRef(null);
    const expensesDropdownRef = useRef(null);

    // File upload state
    const [contractFile, setContractFile] = useState(null);
    const [contractFileName, setContractFileName] = useState('');
    const fileInputRef = useRef(null);

    // Product selection state
    const [availableProducts, setAvailableProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const productDropdownRef = useRef(null);
    const [loadingProductDetails, setLoadingProductDetails] = useState(false);

    // Refs for preventing duplicate submissions
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const lastSubmitTimeRef = useRef(0);
    const lastPartnerDataRef = useRef('');
    const isInitialLoadRef = useRef(true);
    const isClearingRef = useRef(false);

    // ============================================================
    // FILTERED OPTIONS
    // ============================================================
    const filteredPartnershipTypes = partnershipTypeSearchTerm.trim() === ''
        ? partnershipTypeOptions
        : partnershipTypeOptions.filter(type =>
            type.label.toLowerCase().includes(partnershipTypeSearchTerm.toLowerCase())
        );

    const filteredDurations = durationSearchTerm.trim() === ''
        ? durationOptions
        : durationOptions.filter(d =>
            d.value !== '' && d.label.toLowerCase().includes(durationSearchTerm.toLowerCase())
        );

    const filteredExpenses = expensesSearchTerm.trim() === ''
        ? expensesOptions
        : expensesOptions.filter(option =>
            option.label.toLowerCase().includes(expensesSearchTerm.toLowerCase())
        );

    const filteredProducts = productSearchTerm.trim() === ''
        ? availableProducts
        : availableProducts.filter(product =>
            product.product_name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
            (product.batch_no && product.batch_no.toLowerCase().includes(productSearchTerm.toLowerCase())) ||
            (product.barcode && product.barcode.toLowerCase().includes(productSearchTerm.toLowerCase())) ||
            (product.category && product.category.toLowerCase().includes(productSearchTerm.toLowerCase()))
        );

    // ============================================================
    // SHOW TOAST
    // ============================================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    // ============================================================
    // THEME & DARK MODE
    // ============================================================
    const getActiveThemeColor = () => {
        const rootStyle = getComputedStyle(document.documentElement);
        const docPrimary = rootStyle.getPropertyValue('--primary-color').trim();
        if (docPrimary && /^#[0-9A-F]{6}$/i.test(docPrimary)) {
            return docPrimary;
        }
        const storedTheme = localStorage.getItem('ourzest_theme');
        if (storedTheme && /^#[0-9A-F]{6}$/i.test(storedTheme)) {
            return storedTheme;
        }
        return '#6366f1';
    };

    const applyTheme = useCallback((color) => {
        const activeColor = color || getActiveThemeColor();
        document.documentElement.style.setProperty('--ozui-primary', activeColor);
        document.documentElement.style.setProperty('--ozui-primary-dark', activeColor);
        document.documentElement.style.setProperty('--ozui-primary-light', activeColor);

        let r, g, b;
        if (activeColor.startsWith('#')) {
            r = parseInt(activeColor.slice(1, 3), 16);
            g = parseInt(activeColor.slice(3, 5), 16);
            b = parseInt(activeColor.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }

        let style = document.getElementById('dynamic-orb-styles-partners');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-partners';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;

        setCurrentThemeColor(activeColor);
    }, []);

    useEffect(() => {
        const syncTheme = () => {
            const activeColor = getActiveThemeColor();
            applyTheme(activeColor);
        };

        syncTheme();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') syncTheme();
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        const themeInterval = setInterval(syncTheme, 300);

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(themeInterval);
        };
    }, [applyTheme]);

    // ============================================================
    // DARK MODE SYNC
    // ============================================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isBodyDark = document.body.classList.contains('dark-mode') || document.body.classList.contains('ozui-dark');
            const isStorageDark = localStorage.getItem('ourzest_darkMode') === 'true';
            const shouldBeDark = isBodyDark || isStorageDark;

            setDarkMode(shouldBeDark);

            const container = document.querySelector('.ozui-container');
            if (container) {
                if (shouldBeDark) container.classList.add('ozui-dark');
                else container.classList.remove('ozui-dark');
            }
        };

        syncDarkMode();

        const observer = new MutationObserver(() => {
            syncDarkMode();
        });

        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            observer.disconnect();
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // ============================================================
    // LANGUAGE SYNC
    // ============================================================
    useEffect(() => {
        const contextLanguage = normalizeLanguage(
            auth?.currentLanguage ||
            auth?.language ||
            user?.language ||
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') ||
            'en'
        );

        if (contextLanguage !== currentLanguage) {
            setCurrentLanguage(contextLanguage);
        }
    }, [auth?.currentLanguage, auth?.language, user?.language, user?.preferred_language]);

    useEffect(() => {
        const handleLanguageChange = (event) => {
            const newLang = normalizeLanguage(event?.detail?.language || event?.detail?.lang);
            if (newLang && newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
            }
        };

        window.addEventListener('languageChanged', handleLanguageChange);
        window.addEventListener('ourzest:language-change', handleLanguageChange);
        window.addEventListener('storage', (e) => {
            if (e.key === 'ourzest_language') {
                const newLang = normalizeLanguage(e.newValue);
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                }
            }
        });

        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
            window.removeEventListener('ourzest:language-change', handleLanguageChange);
        };
    }, [currentLanguage]);

    // ============================================================
    // RTL DIRECTION
    // ============================================================
    useEffect(() => {
        document.documentElement.lang = currentLanguage;
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
        const container = document.querySelector('.ozui-container');
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
    }, [currentLanguage, isRTL]);

    // ============================================================
    // PRODUCT FETCHING
    // ============================================================
    const fetchProducts = useCallback(async () => {
        if (!currentShop) return;

        setLoadingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user?.username || '',
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });

            const responseText = await response.text();

            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error:', responseText);
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success') {
                setAvailableProducts(data.data || []);
            } else {
                showToast('error', data.message || 'Failed to load products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showToast('error', 'Failed to load products');
        } finally {
            setLoadingProducts(false);
        }
    }, [currentShop, user?.username, currentLanguage, showToast]);

    useEffect(() => {
        if (formData.partnership_type === 'Product Based' && currentShop) {
            fetchProducts();
        } else {
            setAvailableProducts([]);
            setSelectedProducts([]);
            setProductSearchTerm('');
        }
    }, [formData.partnership_type, currentShop, fetchProducts]);

    // ============================================================
    // PRODUCT SELECTION HANDLERS
    // ============================================================
    const toggleProductSelection = (product) => {
        setSelectedProducts(prev => {
            const exists = prev.some(p => p.id === product.id);
            if (exists) {
                return prev.filter(p => p.id !== product.id);
            } else {
                return [...prev, product];
            }
        });
    };

    const toggleSelectAllProducts = () => {
        if (selectedProducts.length === filteredProducts.length && filteredProducts.length > 0) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts([...filteredProducts]);
        }
    };

    const removeProduct = (productId) => {
        setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    };

    const clearSelectedProducts = () => {
        setSelectedProducts([]);
    };

    // ============================================================
    // SELECT HANDLERS
    // ============================================================
    const selectPartnershipType = (type) => {
        setFormData(prev => ({ ...prev, partnership_type: type.value }));
        setPartnershipTypeSearchTerm(type.label);
        setShowPartnershipTypeDropdown(false);
    };

    const selectDuration = (duration) => {
        setFormData(prev => ({ ...prev, partnership_duration: duration.value }));
        setDurationSearchTerm(duration.label);
        setShowDurationDropdown(false);
    };

    const selectExpensesOption = (option) => {
        setFormData(prev => ({ ...prev, expenses_salaries_included: option.value }));
        setExpensesSearchTerm(option.label);
        setShowExpensesDropdown(false);
        if (errors.expenses_salaries_included) {
            setErrors(prev => ({ ...prev, expenses_salaries_included: '' }));
        }
    };

    // ============================================================
    // INPUT CHANGE HANDLERS
    // ============================================================
    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            if (files && files[0]) {
                setContractFile(files[0]);
                setContractFileName(files[0].name);
                setFormData(prev => ({
                    ...prev,
                    partnership_contract: files[0].name
                }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
            if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // ============================================================
    // HANDLE FILE UPLOAD
    // ============================================================
    const handleFileUpload = async (file) => {
        if (!file) return null;

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('action', 'upload_contract');
            formData.append('username', user?.username || '');
            formData.append('shop_id', currentShop?.shop_id || '');

            const response = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                method: 'POST',
                body: formData
            });

            const responseText = await response.text();

            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error. Raw response:', responseText.substring(0, 500));
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success') {
                return data.file_path;
            } else {
                showToast('error', data.message || 'Failed to upload contract');
                return null;
            }
        } catch (error) {
            console.error('File upload error:', error);
            showToast('error', 'Network error while uploading file');
            return null;
        }
    };

    // ============================================================
    // VALIDATE FORM
    // ============================================================
    const validateForm = () => {
        const newErrors = {};

        if (!formData.partner_name?.trim()) {
            newErrors.partner_name = t.nameRequired || 'Partner name is required';
        } else if (formData.partner_name.trim().length < 2) {
            newErrors.partner_name = t.nameTooShort || 'Name must be at least 2 characters';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t.invalidEmail || 'Please enter a valid email address';
        }

        if (formData.investment_amount && parseFloat(formData.investment_amount) < 0) {
            newErrors.investment_amount = t.invalidInvestment || 'Investment amount cannot be negative';
        }

        if (formData.profit_percentage && (parseFloat(formData.profit_percentage) < 0 || parseFloat(formData.profit_percentage) > 100)) {
            newErrors.profit_percentage = t.invalidProfit || 'Profit percentage must be between 0 and 100';
        }

        if (formData.contract_start_date && formData.contract_end_date) {
            const start = new Date(formData.contract_start_date);
            const end = new Date(formData.contract_end_date);
            if (end < start) {
                newErrors.contract_end_date = t.contractEndBeforeStart || 'Contract end date cannot be before start date';
            }
        }

        if (formData.partnership_type === 'Product Based' && selectedProducts.length === 0) {
            newErrors.products = 'Please select at least one product for product-based partnership';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ============================================================
    // LOAD PARTNER DATA
    // ============================================================
    const loadPartnerData = async (id) => {
        if (!currentShop) {
            showToast('error', t.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get',
                    partner_id: id,
                    username: user?.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });

            const responseText = await response.text();

            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error:', responseText);
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success' && data.partner) {
                const partner = { ...data.partner, ...(data.partner._original || {}) };
                setFormData(prev => ({
                    ...prev,
                    partner_name: partner.partner_name || '',
                    phone: partner.phone || '',
                    email: partner.email || '',
                    address: partner.address || '',
                    partnership_type: partner.partnership_type || 'ALL',
                    partnership_duration: partner.partnership_duration || '',
                    partnership_contract: partner.partnership_contract || null,
                    investment_amount: partner.investment_amount || '',
                    profit_percentage: partner.profit_percentage || '',
                    description: partner.description || '',
                    notes: partner.notes || '',
                    contract_start_date: partner.contract_start_date || '',
                    contract_end_date: partner.contract_end_date || '',
                    status: partner.status || 'active',
                    expenses_salaries_included: partner.expenses_salaries_included == 1
                }));

                const selectedType = partnershipTypeOptions.find(t => t.value === partner.partnership_type);
                setPartnershipTypeSearchTerm(selectedType ? selectedType.label : '');

                const selectedDuration = durationOptions.find(d => d.value === partner.partnership_duration);
                setDurationSearchTerm(selectedDuration ? selectedDuration.label : '');

                const selectedExpenses = expensesOptions.find(e => e.value === (partner.expenses_salaries_included == 1));
                setExpensesSearchTerm(selectedExpenses ? selectedExpenses.label : '');

                if (partner.partnership_contract) {
                    setContractFileName(partner.partnership_contract);
                }

                if (partner.partnership_type === 'Product Based' && partner.selected_products) {
                    const productIds = partner.selected_products.split(',').map(id => parseInt(id.trim()));
                    const validIds = productIds.filter(id => !isNaN(id) && id > 0);

                    if (validIds.length > 0) {
                        setLoadingProductDetails(true);
                        try {
                            const productResponse = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    action: 'get_product_details',
                                    product_ids: validIds,
                                    username: user?.username,
                                    shop_id: currentShop.shop_id,
                                    language: currentLanguage
                                })
                            });

                            const productText = await productResponse.text();
                            let productData;
                            try {
                                productData = JSON.parse(productText);
                            } catch (e) {
                                console.error('Product parse error:', productText);
                                productData = { status: 'error' };
                            }

                            if (productData.status === 'success' && productData.data) {
                                setSelectedProducts(productData.data);
                            } else if (partner.product_details && partner.product_details.length > 0) {
                                setSelectedProducts(partner.product_details);
                            } else {
                                setSelectedProducts(validIds.map(id => ({ id, product_name: `Product #${id}` })));
                            }
                        } catch (err) {
                            console.error('Error loading product details:', err);
                            setSelectedProducts(validIds.map(id => ({ id, product_name: `Product #${id}` })));
                        } finally {
                            setLoadingProductDetails(false);
                        }
                    }
                }
            } else {
                showToast('error', data.message || 'Failed to load partner data');
            }
        } catch (error) {
            console.error('Load partner error:', error);
            showToast('error', t.networkError || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // HANDLE SUBMIT
    // ============================================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentShop) {
            showToast('error', t.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        if (!validateForm()) return;

        if (isSubmittingRef.current) {
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        const now = Date.now();
        if (now - lastSubmitTimeRef.current < 3000) {
            showToast('warning', 'Please wait before submitting again');
            return;
        }

        const currentPartnerData = JSON.stringify({
            partner_name: formData.partner_name,
            phone: formData.phone,
            email: formData.email,
            shop_id: currentShop.shop_id
        });

        if (!isEditMode && currentPartnerData === lastPartnerDataRef.current) {
            showToast('warning', 'This partner appears to be a duplicate. Please check.');
            return;
        }

        lastPartnerDataRef.current = currentPartnerData;
        lastSubmitTimeRef.current = now;

        isSubmittingRef.current = true;
        setLoading(true);

        const submitButton = document.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        try {
            const currentUsername = user?.username || 'Unknown';

            let contractPath = formData.partnership_contract;
            if (contractFile) {
                const uploadedPath = await handleFileUpload(contractFile);
                if (uploadedPath) {
                    contractPath = uploadedPath;
                } else {
                    showToast('warning', 'Contract upload failed, but partner will be saved');
                }
            }

            const selectedProductIds = selectedProducts.map(p => p.id);

            const partnerData = {
                action: isEditMode ? 'update' : 'add',
                partner_name: formData.partner_name,
                phone: formData.phone || null,
                email: formData.email || null,
                address: formData.address || null,
                partnership_type: formData.partnership_type || 'ALL',
                partnership_duration: formData.partnership_duration || null,
                partnership_contract: contractPath || null,
                investment_amount: formData.investment_amount || 0,
                profit_percentage: formData.profit_percentage || 0,
                description: formData.description || null,
                notes: formData.notes || null,
                contract_start_date: formData.contract_start_date || null,
                contract_end_date: formData.contract_end_date || null,
                status: formData.status || 'active',
                expenses_salaries_included: formData.expenses_salaries_included || false,
                username: currentUsername,
                shop_id: currentShop.shop_id,
                shop_name: currentShop.shop_name || null,
                shop_code: currentShop.shop_code || null,
                added_by_user_id: user?.id || null,
                added_by_username: currentUsername,
                selected_products: selectedProductIds,
                product_selection_mode: 'multiple',
                language: currentLanguage,
                _timestamp: Date.now()
            };

            if (isEditMode && partnerId) {
                partnerData.partner_id = partnerId;
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(partnerData),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server responded with status ${response.status}: ${errorText.substring(0, 200)}`);
            }

            const responseText = await response.text();

            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error. Raw response:', responseText.substring(0, 500));
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success') {
                showToast('success', isEditMode
                    ? (t.partnerUpdated || 'Partner updated successfully!')
                    : (t.partnerSaved || 'Partner saved successfully!'));

                if (!isEditMode) {
                    setFormData({
                        partner_name: '',
                        phone: '',
                        email: '',
                        address: '',
                        partnership_type: 'ALL',
                        partnership_duration: '',
                        partnership_contract: null,
                        investment_amount: '',
                        profit_percentage: '',
                        description: '',
                        notes: '',
                        contract_start_date: '',
                        contract_end_date: '',
                        status: 'active',
                        expenses_salaries_included: false,
                        themeColor: formData.themeColor
                    });
                    setPartnershipTypeSearchTerm('');
                    setDurationSearchTerm('');
                    setExpensesSearchTerm('');
                    setContractFile(null);
                    setContractFileName('');
                    setSelectedProducts([]);
                    setProductSearchTerm('');
                    setErrors({});
                    lastPartnerDataRef.current = '';

                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }

                    setTimeout(() => {
                        if (partnerNameRef.current) {
                            partnerNameRef.current.focus();
                        }
                    }, 100);
                }

                if (onSuccess) onSuccess(data.partner_id || partnerId);
            } else {
                showToast('error', data.message || (isEditMode
                    ? 'Failed to update partner'
                    : 'Failed to save partner'));
                lastPartnerDataRef.current = '';
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                showToast('error', 'Request timeout. Please try again.');
            } else {
                console.error('Submit error:', error);
                showToast('error', error.message || t.networkError || 'Network error occurred');
            }
            lastPartnerDataRef.current = '';
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
                if (submitButton) submitButton.disabled = false;
            }, 3000);
        }
    };

    // ============================================================
    // HANDLE CLEAR
    // ============================================================
    const handleClear = () => {
        isClearingRef.current = true;

        setFormData({
            partner_name: '',
            phone: '',
            email: '',
            address: '',
            partnership_type: 'ALL',
            partnership_duration: '',
            partnership_contract: null,
            investment_amount: '',
            profit_percentage: '',
            description: '',
            notes: '',
            contract_start_date: '',
            contract_end_date: '',
            status: 'active',
            expenses_salaries_included: false,
            themeColor: formData.themeColor
        });
        setPartnershipTypeSearchTerm('');
        setDurationSearchTerm('');
        setExpensesSearchTerm('');
        setContractFile(null);
        setContractFileName('');
        setSelectedProducts([]);
        setProductSearchTerm('');
        setErrors({});
        setIsEditMode(false);
        lastPartnerDataRef.current = '';

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        showToast('success', 'Form cleared');

        setTimeout(() => {
            if (partnerNameRef.current) {
                partnerNameRef.current.focus();
            }
        }, 100);

        setTimeout(() => {
            isClearingRef.current = false;
        }, 500);
    };

    // ============================================================
    // HANDLE SWITCH SHOP
    // ============================================================
    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                setPartnershipTypeSearchTerm('');
                setDurationSearchTerm('');
                setExpensesSearchTerm('');
                setContractFile(null);
                setContractFileName('');
                setSelectedProducts([]);
                setProductSearchTerm('');
                setAvailableProducts([]);
                setFormData(prev => ({
                    ...prev,
                    partner_name: '',
                    phone: '',
                    email: '',
                    address: '',
                    partnership_type: 'ALL',
                    partnership_duration: '',
                    partnership_contract: null,
                    investment_amount: '',
                    profit_percentage: '',
                    description: '',
                    notes: '',
                    contract_start_date: '',
                    contract_end_date: '',
                    status: 'active',
                    expenses_salaries_included: false
                }));
            }
            showToast(success ? 'success' : 'error',
                success ? (t.shopSwitched || 'Shop switched successfully')
                    : (t.shopSwitchFailed || 'Failed to switch shop'));
        } catch (error) {
            showToast('error', t.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // ============================================================
    // EFFECTS
    // ============================================================

    // Click outside handlers for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (partnershipTypeDropdownRef.current && !partnershipTypeDropdownRef.current.contains(event.target)) {
                setShowPartnershipTypeDropdown(false);
            }
            if (durationDropdownRef.current && !durationDropdownRef.current.contains(event.target)) {
                setShowDurationDropdown(false);
            }
            if (expensesDropdownRef.current && !expensesDropdownRef.current.contains(event.target)) {
                setShowExpensesDropdown(false);
            }
            if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auto-focus
    useEffect(() => {
        setTimeout(() => {
            if (partnerNameRef.current) {
                partnerNameRef.current.focus();
            }
        }, 100);
    }, []);

    // Cleanup
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // Load partner if editing
    useEffect(() => {
        if (partnerId) {
            setIsEditMode(true);
            loadPartnerData(partnerId);
        }
    }, [partnerId, currentShop]);

    // Load shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // ============================================================
    // RENDER SHOP INDICATOR
    // ============================================================
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozui-shop-indicator no-shop">
                    <div className="ozui-shop-badge">
                        <span className="ozui-shop-warning">
                            {t.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} isRTL={isRTL} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                t={t}
                loading={loadingShops}
                isRTL={isRTL}
            />

            {/* Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>
                                {isEditMode ? (t.editPartner || 'Edit Partner') : (t.addPartner || 'Add Partner')}
                            </h1>
                            <p>{t.fillDetails || 'Fill in the partner details below'}</p>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        {renderShopIndicator()}
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={handleClear}
                                disabled={loading}
                                title={t.clearFormTooltip || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{t.clearButton || 'Clear'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    {/* Partner Name */}
                    <div className={`ozui-field ${errors.partner_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={partnerNameRef}
                                type="text"
                                name="partner_name"
                                id="partner_name"
                                value={formData.partner_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="partner_name">
                                <FontAwesomeIcon icon={faUserTie} />
                                {t.partnerName || 'Partner Name'} <span>*</span>
                            </label>
                        </div>
                        {errors.partner_name && <small>{errors.partner_name}</small>}
                    </div>

                    {/* Phone Number */}
                    <div className={`ozui-field ${errors.phone ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir="ltr"
                            />
                            <label htmlFor="phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {t.phone || 'Phone Number'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.phone && <small>{errors.phone}</small>}
                    </div>

                    {/* Email */}
                    <div className={`ozui-field ${errors.email ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir="ltr"
                            />
                            <label htmlFor="email">
                                <FontAwesomeIcon icon={faEnvelope} />
                                {t.email || 'Email Address'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.email && <small>{errors.email}</small>}
                    </div>

                    {/* Address */}
                    <div className={`ozui-field ${errors.address ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="2"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="address">
                                <FontAwesomeIcon icon={faAddressCard} />
                                {t.address || 'Address'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.address && <small>{errors.address}</small>}
                    </div>

                    {/* Partnership Type */}
                    <div className={`ozui-field ${errors.partnership_type ? 'ozui-error' : ''}`} ref={partnershipTypeDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="partnership_type"
                                value={partnershipTypeSearchTerm}
                                onChange={(e) => {
                                    setPartnershipTypeSearchTerm(e.target.value);
                                    setShowPartnershipTypeDropdown(true);
                                    if (formData.partnership_type && !partnershipTypeOptions.find(t =>
                                        t.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, partnership_type: '' }));
                                    }
                                }}
                                onFocus={() => setShowPartnershipTypeDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="partnership_type">
                                <FontAwesomeIcon icon={faHandshake} />
                                {t.partnershipType || 'Partnership Type'} <span>*</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.partnership_type && <small>{errors.partnership_type}</small>}
                        {showPartnershipTypeDropdown && (
                            <div className="ozui-category-dropdown" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                                {filteredPartnershipTypes.length > 0 ? (
                                    filteredPartnershipTypes.map(type => (
                                        <div
                                            key={type.value}
                                            className={`ozui-category-option ${formData.partnership_type === type.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectPartnershipType(type)}
                                        >
                                            {type.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">{t.noTypeFound || 'No types found'}</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Partnership Duration */}
                    <div className={`ozui-field ${errors.partnership_duration ? 'ozui-error' : ''}`} ref={durationDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="partnership_duration"
                                value={durationSearchTerm}
                                onChange={(e) => {
                                    setDurationSearchTerm(e.target.value);
                                    setShowDurationDropdown(true);
                                    if (formData.partnership_duration && !durationOptions.find(d =>
                                        d.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, partnership_duration: '' }));
                                    }
                                }}
                                onFocus={() => setShowDurationDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="partnership_duration">
                                <FontAwesomeIcon icon={faClock} />
                                {t.partnershipDuration || 'Partnership Duration'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.partnership_duration && <small>{errors.partnership_duration}</small>}
                        {showDurationDropdown && (
                            <div className="ozui-category-dropdown" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                                {filteredDurations.length > 0 ? (
                                    filteredDurations.map(duration => (
                                        <div
                                            key={duration.value}
                                            className={`ozui-category-option ${formData.partnership_duration === duration.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectDuration(duration)}
                                        >
                                            {duration.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">{t.noDurationFound || 'No durations found'}</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Contract Start Date */}
                    <div className={`ozui-field ${errors.contract_start_date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="contract_start_date"
                                id="contract_start_date"
                                value={formData.contract_start_date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="contract_start_date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {t.contractStartDate || 'Contract Start'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.contract_start_date && <small>{errors.contract_start_date}</small>}
                    </div>

                    {/* Contract End Date */}
                    <div className={`ozui-field ${errors.contract_end_date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="contract_end_date"
                                id="contract_end_date"
                                value={formData.contract_end_date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="contract_end_date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {t.contractEndDate || 'Contract End'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.contract_end_date && <small>{errors.contract_end_date}</small>}
                    </div>

                    {/* Partnership Contract - File Upload */}
                    <div className={`ozui-field ${errors.partnership_contract ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper ozui-file-wrapper">
                            <input
                                type="file"
                                name="partnership_contract"
                                id="partnership_contract"
                                ref={fileInputRef}
                                onChange={handleChange}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                disabled={loading}
                            />
                            <label htmlFor="partnership_contract">
                                <FontAwesomeIcon icon={faFileContract} />
                                {t.partnershipContract || 'Partnership Contract'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                            {contractFileName && (
                                <span className="ozui-file-name">
                                    <FontAwesomeIcon icon={faCheckCircle} className="ozui-file-check" />
                                    {contractFileName}
                                </span>
                            )}
                        </div>
                        {errors.partnership_contract && <small>{errors.partnership_contract}</small>}
                    </div>
                </div>

                <div className="ozui-form-row">
                    {/* Investment Amount */}
                    <div className={`ozui-field ${errors.investment_amount ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="investment_amount"
                                id="investment_amount"
                                value={formData.investment_amount}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                                dir="ltr"
                            />
                            <label htmlFor="investment_amount">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                {t.investmentAmount || 'Investment Amount'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.investment_amount && <small>{errors.investment_amount}</small>}
                    </div>

                    {/* Profit Percentage */}
                    <div className={`ozui-field ${errors.profit_percentage ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="profit_percentage"
                                id="profit_percentage"
                                value={formData.profit_percentage}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder=" "
                                disabled={loading}
                                dir="ltr"
                            />
                            <label htmlFor="profit_percentage">
                                <FontAwesomeIcon icon={faPercent} />
                                {t.profitPercentage || 'Profit %'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.profit_percentage && <small>{errors.profit_percentage}</small>}
                    </div>

                    {/* Status */}
                    <div className={`ozui-field ${errors.status ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <select
                                name="status"
                                id="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={loading}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                <option value="active">{t.active || 'Active'}</option>
                                <option value="inactive">{t.inactive || 'Inactive'}</option>
                                <option value="suspended">{t.suspended || 'Suspended'}</option>
                                <option value="terminated">{t.terminated || 'Terminated'}</option>
                            </select>
                            <label htmlFor="status">
                                <FontAwesomeIcon icon={faIdCard} />
                                {t.status || 'Status'}
                            </label>
                        </div>
                        {errors.status && <small>{errors.status}</small>}
                    </div>
                </div>

                {/* Expenses/Salaries Included */}
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.expenses_salaries_included ? 'ozui-error' : ''}`} ref={expensesDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="expenses_salaries_included"
                                value={expensesSearchTerm}
                                onChange={(e) => {
                                    setExpensesSearchTerm(e.target.value);
                                    setShowExpensesDropdown(true);
                                    if (formData.expenses_salaries_included !== undefined && !expensesOptions.find(opt =>
                                        opt.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, expenses_salaries_included: false }));
                                    }
                                }}
                                onFocus={() => setShowExpensesDropdown(true)}
                                placeholder={t.selectExpensesOption || ''}
                                disabled={loading}
                                autoComplete="off"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="expenses_salaries_included">
                                <FontAwesomeIcon icon={faMoneyCheckDollar} />
                                {t.expensesSalariesIncluded || 'Expenses/Salaries Included'} <span>*</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.expenses_salaries_included && <small>{errors.expenses_salaries_included}</small>}
                        {showExpensesDropdown && (
                            <div className="ozui-category-dropdown" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                                {filteredExpenses.length > 0 ? (
                                    filteredExpenses.map(option => (
                                        <div
                                            key={option.value ? 'true' : 'false'}
                                            className={`ozui-category-option ${formData.expenses_salaries_included === option.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectExpensesOption(option)}
                                        >
                                            {option.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">{t.noOptionFound || 'No options found'}</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Product Selection */}
                {formData.partnership_type === 'Product Based' && (
                    <div className="ozui-product-selection-section">
                        <div className="ozui-section-header">
                            <h4>
                                <FontAwesomeIcon icon={faStore} />
                                {t.selectProducts || 'Select Products'}
                            </h4>
                            <span className="ozui-selected-count">
                                {selectedProducts.length} {t.selected || 'selected'}
                            </span>
                        </div>

                        <div className={`ozui-field ${errors.products ? 'ozui-error' : ''}`} ref={productDropdownRef}>
                            <div className="ozui-input-wrapper ozui-category-wrapper">
                                <input
                                    type="text"
                                    value={productSearchTerm}
                                    onChange={(e) => {
                                        setProductSearchTerm(e.target.value);
                                        setShowProductDropdown(true);
                                    }}
                                    onFocus={() => setShowProductDropdown(true)}
                                    placeholder={t.searchProducts || 'Search products...'}
                                    disabled={loading || loadingProducts}
                                    autoComplete="off"
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label>
                                    <FontAwesomeIcon icon={faSearch} />
                                    {t.searchProducts || 'Search Products'}
                                    {!isRTL && <span> ({t.optional})</span>}
                                </label>
                                {loadingProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-loading-icon" />}
                                <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                            </div>

                            {showProductDropdown && (
                                <div className="ozui-category-dropdown ozui-product-dropdown" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                                    {loadingProducts ? (
                                        <div className="ozui-dropdown-loading">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            {t.loadingProducts || 'Loading products...'}
                                        </div>
                                    ) : filteredProducts.length > 0 ? (
                                        <>
                                            <div className="ozui-dropdown-header">
                                                <button
                                                    className="ozui-select-all-btn"
                                                    onClick={toggleSelectAllProducts}
                                                    type="button"
                                                >
                                                    {selectedProducts.length === filteredProducts.length && filteredProducts.length > 0
                                                        ? (t.deselectAll || 'Deselect All')
                                                        : (t.selectAll || 'Select All')}
                                                </button>
                                                <span className="ozui-product-count">
                                                    {filteredProducts.length} {t.products || 'products'}
                                                </span>
                                            </div>
                                            <div className="ozui-product-list">
                                                {filteredProducts.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className={`ozui-product-option ${selectedProducts.some(p => p.id === product.id) ? 'ozui-selected' : ''}`}
                                                        onClick={() => toggleProductSelection(product)}
                                                    >
                                                        <div className="ozui-product-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedProducts.some(p => p.id === product.id)}
                                                                onChange={() => toggleProductSelection(product)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div className="ozui-product-info">
                                                            <span className="ozui-product-name">{product.product_name}</span>
                                                            {product.batch_no && (
                                                                <span className="ozui-product-batch">{t.productBatch || 'Batch'}: {product.batch_no}</span>
                                                            )}
                                                            {product.quantity !== undefined && (
                                                                <span className="ozui-product-qty">{t.productQty || 'Qty'}: {product.quantity}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="ozui-dropdown-empty">
                                            {availableProducts.length === 0
                                                ? (t.noProductsAvailable || 'No products available. Please add products first.')
                                                : (t.noProductsFound || 'No products found matching your search.')}
                                        </div>
                                    )}
                                </div>
                            )}
                            {errors.products && <small>{errors.products}</small>}
                        </div>

                        {selectedProducts.length > 0 && (
                            <div className="ozui-selected-products">
                                <div className="ozui-selected-header">
                                    <span className="ozui-selected-title">
                                        {t.selectedProducts || 'Selected Products:'}
                                    </span>
                                    <button
                                        className="ozui-clear-selected-btn"
                                        onClick={clearSelectedProducts}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                        {t.clearAll || 'Clear All'}
                                    </button>
                                </div>
                                <div className="ozui-product-tags">
                                    {selectedProducts.map(product => (
                                        <div key={product.id} className="ozui-product-tag">
                                            <span className="ozui-tag-text">{product.product_name}</span>
                                            {product.batch_no && (
                                                <span className="ozui-tag-batch">{product.batch_no}</span>
                                            )}
                                            <button
                                                className="ozui-tag-remove"
                                                onClick={() => removeProduct(product.id)}
                                                type="button"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="ozui-form-row">
                    {/* Description */}
                    <div className={`ozui-field ${errors.description ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                name="description"
                                id="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="3"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="description">
                                <FontAwesomeIcon icon={faAlignLeft} />
                                {t.description || 'Description'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.description && <small>{errors.description}</small>}
                    </div>

                    {/* Notes */}
                    <div className={`ozui-field ${errors.notes ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                name="notes"
                                id="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="3"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="notes">
                                <FontAwesomeIcon icon={faStickyNote} />
                                {t.notes || 'Notes / Remarks'}
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.notes && <small>{errors.notes}</small>}
                    </div>
                </div>

                {/* Form Actions */}
                <div className="ozui-form-actions">
                    <button
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={loading || !currentShop || isSubmittingRef.current}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{isEditMode ? (t.updating || 'Updating...') : (t.adding || 'Adding...')}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (t.updatePartner || 'Update Partner') : (t.savePartner || 'Save Partner')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SolarPartners;